import { Injectable, inject } from '@angular/core';
import {HttpHeaderService} from './http-header.service';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {APP_CONFIG} from '../config/app-config.token';
import {AppConfig} from '../config/app-config.model';
import {ElectronicDocumentReference} from '../model/electronic-document-reference';
import {CapacitorHttpService} from './capacitor-http.service';

export interface DeclaredSituationEntry {
  MainSituation?: string;
  SubSituation?: string;
  [key: string]: unknown;
}

/**
 * One day of the C3/C3.2 calendar. C3 documents use DeclSit, C3.2 use
 * DeclaredSituation. The backend converts XML via org.json XML.toJSONObject,
 * which collapses a single repeated element to a bare object — so Situation
 * (and Sit itself) can arrive as an object instead of an array.
 */
export interface ElectronicDocumentDay {
  Date: string;
  DeclSit?: DeclaredSituationEntry;
  DeclaredSituation?: { Situation: DeclaredSituationEntry | DeclaredSituationEntry[] };
}

/**
 * Raw confirmed-document payload as returned by the electronic documents backend.
 * Only the fields the frontend actually reads are typed; the rest stays open.
 */
export interface ConfirmedElectronicDocument {
  // XML.toJSONObject coerces digit-only strings, so INSS arrives as a number.
  INSS: string | number;
  YearMonth: string;
  ConfD: string;
  Cmt: string;
  Sit: ElectronicDocumentDay | ElectronicDocumentDay[];
  [key: string]: unknown;
}

export interface ElectronicDocumentDetail {
  confirmedC3?: ConfirmedElectronicDocument;
  confirmedC32?: ConfirmedElectronicDocument;
}

@Injectable({
  providedIn: 'root'
})
export class ElectronicDocumentsService {
  private http = inject(HttpClient);
  private capacitorHttp = inject(CapacitorHttpService);
  private httpHeaderService = inject(HttpHeaderService);
  private config = inject<AppConfig>(APP_CONFIG);

  baseurl: string;

  constructor() {
    this.baseurl = this.config.apiGatewayUrl;
  }

  getElectronicDocumentsOverview(inss: string, documentType: string,
                                 startMonth: string, endMonth: string ): Observable<ElectronicDocumentReference[]> {
    return this.capacitorHttp.get<ElectronicDocumentReference[]>( this.baseurl +
      `/electronicdocuments/overview/${inss}?documentType=E${documentType}&startMonth=${startMonth}&endMonth=${endMonth}`,
      this.httpHeaderService.getHeaderOptions());
  }

  getElectronicDocument(ticketNumber: string, startMonthStr: string, endMonthStr: string,
                        selectedType: string, memberInss: string): Observable<ElectronicDocumentDetail> {
    return this.capacitorHttp.get<ElectronicDocumentDetail>( this.baseurl +
      `/electronicdocuments/${ticketNumber}?startMonth=${startMonthStr}&endMonth=${endMonthStr}` +
      `&documentType=${selectedType}&ssin=${memberInss}`,
      this.httpHeaderService.getHeaderOptions());
  }

  // ========== METHODS FOR EXTERNAL USERS (using /me endpoints) ==========
  // These methods are for external users (members) who can only access their own data.
  // Tyk API Gateway rewrites /members/me to /members/{jwt.preferred_username}

  /**
   * Get electronic documents overview for authenticated external user
   * @param documentType Document type
   * @param startMonth Start month in format YYYY-MM
   * @param endMonth End month in format YYYY-MM
   * @returns Observable<ElectronicDocumentReference[]>
   */
  getMyElectronicDocumentsOverview(documentType: string, startMonth: string, endMonth: string): Observable<ElectronicDocumentReference[]> {
    return this.capacitorHttp.get<ElectronicDocumentReference[]>(
      this.baseurl + `/electronicdocuments/overview/me?documentType=E${documentType}&startMonth=${startMonth}&endMonth=${endMonth}`,
      this.httpHeaderService.getHeaderOptions()
    );
  }

  /**
   * Get electronic document for authenticated external user
   * Note: This endpoint uses ticket number and may not require /me rewriting
   * @param ticketNumber Ticket number
   * @param startMonthStr Start month
   * @param endMonthStr End month
   * @param selectedType Selected document type
   * @param memberInss Member INSS (may still be required for this endpoint)
   * @returns Observable<ElectronicDocumentDetail>
   */
  getMyElectronicDocument(ticketNumber: string, startMonthStr: string, endMonthStr: string,
                          selectedType: string, memberInss: string): Observable<ElectronicDocumentDetail> {
    return this.capacitorHttp.get<ElectronicDocumentDetail>(
      this.baseurl + `/electronicdocuments/${ticketNumber}?startMonth=${startMonthStr}&endMonth=${endMonthStr}` +
        `&documentType=${selectedType}&ssin=${memberInss}`,
      this.httpHeaderService.getHeaderOptions()
    );
  }
}
